// Doc Order — Workspace orchestrator (processor draft · closing review · LO sign).
// Renders the 5-step visual stepper, the sections in each role's order, the
// role/stage-aware action bar, and the handoff modals whose email previews come
// straight from the shared core builders (preview == send, structurally). The
// viewer role is derived from server capabilities + stage — the client never
// invents identity binding; editability flows from core `isEditable`.
import { useEffect, useMemo, useState } from 'react';
import type { ProspectLoan } from '../types';
import { Btn, Icon } from './atoms';
import { DocOrderProvider, type DocOrderContextValue } from './context';
import { fmtDate, fmtMoney, fmtStamp } from './format';
import { buildLoanFacts, buildSnapshotExtras } from './viewModel';
import type { UseDocOrderResult } from './useDocOrder';
import type { DocOrderRow } from '@/services/docOrderService';
import {
  buildE1Submitted,
  buildE2SentToLo,
  canDeferInsurance as canDeferInsuranceGate,
  DO_CONFIG,
  DO_VISUAL_STAGES,
  feeDispositionBlocker,
  feeReconciliationBlocker,
  feeNeedsDisposition,
  netLoPayout,
  reimburseLines as coreReimburseLines,
  reimburseTotal as coreReimburseTotal,
  reqFilled as reqFilledCount,
  section4Gate,
  section5Gate,
  section6Gate,
  sendToLoGate,
  visualStageIndex,
  type ChecklistDoc,
  type DocOrderFee,
  type DocOrderRole,
  type DocOrderStage,
  type DocOrderViewer,
  type InsuranceInfo,
  type ManualClosingFields,
  type OutstandingItem
} from './core';
import { SnapshotSection } from './sections/Snapshot';
import { ClosingVestingSection } from './sections/ClosingVesting';
import { SettlementSection } from './sections/Settlement';
import { InsuranceSection } from './sections/Insurance';
import { FeeTable } from './sections/FeeTable';
import { DocumentsSection } from './sections/Documents';
import { ClosingReviewSummary } from './sections/ClosingReviewSummary';
import { LoSummary } from './sections/LoSummary';
import { SignOffSection, type SignOffCheck } from './sections/SignOff';

const STAGE_ICONS: Record<string, string> = {
  draft: 'edit',
  submitted: 'send',
  confirmed: 'fileCheck',
  lo_review: 'eye',
  complete: 'checkCircle'
};

const SECTION_ORDERS: Record<DocOrderRole, string[]> = {
  // Processor builds top-to-bottom in the natural intake order.
  processor: ['sec-snapshot', 'sec-closing', 'sec-settlement', 'sec-insurance', 'sec-fees', 'sec-docs'],
  // Closing reviews closing details + fees, then a consolidated read-only review.
  closing: ['sec-closing', 'sec-fees', 'sec-review'],
  // LO is handled by the LoSummary sheet; this order is only used for its stepNos.
  lo: ['sec-fees', 'sec-snapshot', 'sec-closing', 'sec-settlement', 'sec-insurance', 'sec-docs']
};

/**
 * Derive the viewer's layout role from server capabilities + the order stage.
 * A pure closer/processor/LO reads their single capability; an elevated
 * admin/manager (all caps) follows the stage so they see the stage-appropriate
 * editor view. The server re-checks the real identity gate on every action.
 */
export function deriveRole(viewer: DocOrderViewer, stage: DocOrderStage): DocOrderRole {
  if (viewer.isElevated) {
    if (stage === 'lo_review' || stage === 'complete') {
      return 'lo';
    }
    if (stage === 'submitted') {
      return 'closing';
    }
    return 'processor';
  }
  if ((stage === 'lo_review' || stage === 'complete') && viewer.canActAsLo) {
    return 'lo';
  }
  if (viewer.canReviewSubmitted) {
    return 'closing';
  }
  if (viewer.canEditDraft) {
    return 'processor';
  }
  if (viewer.canActAsLo) {
    return 'lo';
  }
  return 'processor';
}

const EMPTY_INSURANCE: InsuranceInfo = {
  company: null,
  premium: null,
  effective: null,
  expiration: null,
  phone: null,
  email: null,
  hoaMasterCoversWallsIn: false,
  dicExtendedCoverages: false,
  dicCompany: null,
  dicPremium: null,
  dicEffective: null,
  dicExpiration: null,
  dicPhone: null,
  dicEmail: null,
  deferred: false,
  deferReason: null
};

interface WorkspaceProps {
  docOrder: UseDocOrderResult;
  loan: ProspectLoan;
  userName: string;
  addToast: (message: string, icon?: string) => void;
}

type ModalType = 'submit' | 'confirm' | 'ack' | null;

export function Workspace({ docOrder, loan, userName, addToast }: WorkspaceProps) {
  const { order, fees, derivedDocs, invoiceFolderStatus, invoiceFolderDocs, viewer } = docOrder;
  const loadLoSummary = docOrder.loadLoSummary;
  const [signBlockers, setSignBlockers] = useState<string[]>([]);
  const [modal, setModal] = useState<ModalType>(null);
  // R5 #13: the closer's read-only Preview Doc Order modal (LoSummary, inert).
  const [previewOpen, setPreviewOpen] = useState(false);
  // R7: reopen a SIGNED (complete) order — closer/elevated, reason required.
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const now = useMemo(() => new Date(), []);

  const summaryRole = order && viewer ? deriveRole(viewer, order.stage) : null;
  const autoLoadLoSummary =
    !!order &&
    !!viewer &&
    (order.stage === 'lo_review' || order.stage === 'complete') &&
    (summaryRole === 'lo' || viewer.canReviewSubmitted);
  useEffect(() => {
    if (autoLoadLoSummary) {
      void loadLoSummary();
    }
  }, [autoLoadLoSummary, loadLoSummary, order?.id, order?.revision]);

  // order is guaranteed non-null here (DocOrderTab renders Workspace only when
  // an order exists), but keep a guard for type-narrowing.
  if (!order || !viewer) {
    return null;
  }

  const role = summaryRole ?? deriveRole(viewer, order.stage);
  // A1.1 — the processor owns Draft; the closer owns Submitted. Round-6 #2:
  // a reload-required workspace is provably stale — every input goes
  // read-only until convergence succeeds (the hook also hard-gates writes).
  const editable =
    !docOrder.needsReload &&
    ((role === 'processor' && order.stage === 'draft' && viewer.canEditDraft) ||
      (role === 'closing' && order.stage === 'submitted' && viewer.canReviewSubmitted));
  const loanFacts = buildLoanFacts(order, loan);
  const extras = buildSnapshotExtras(loan);

  const ctxValue: DocOrderContextValue = {
    editable,
    stage: order.stage,
    role,
    viewer,
    loanFacts,
    closingDate: order.closing_date,
    userName,
    invoiceFolderStatus,
    invoiceFolderDocs,
    now,
    addToast,
    patchClosing: docOrder.patchClosing,
    patchSettlement: docOrder.patchSettlement,
    patchInsurance: docOrder.patchInsurance,
    patchVesting: docOrder.patchVesting,
    saveFee: docOrder.saveFee,
    addManualFee: docOrder.addManualFee,
    flush: docOrder.flush,
    saveFeeNow: docOrder.saveFeeNow,
    escalateFee: docOrder.escalateFee,
    undoEscalation: docOrder.undoEscalation,
    setFirstPaymentOverride: docOrder.setFirstPaymentOverride,
    saveStalled: docOrder.saveStalled,
    writebacks: docOrder.writebacks,
    busy: docOrder.busy,
    deferInsurance: docOrder.deferInsurance,
    closingEditFee: docOrder.closingEditFee,
    approveFee: docOrder.approveFee,
    vestingAttest: docOrder.vestingAttest,
    retryWrites: docOrder.retryWrites,
    setLenderCredit: docOrder.setLenderCredit,
    escalateLenderCredit: docOrder.escalateLenderCredit,
    withdrawLenderCreditEscalation: docOrder.withdrawLenderCreditEscalation,
    loRequestChanges: docOrder.loRequestChanges,
    loAcknowledge: docOrder.loAcknowledge,
    downloadPdf: docOrder.downloadPdf,
    uploadFile: docOrder.uploadFile,
    commitMarks: docOrder.commitMarks,
    resetDocument: docOrder.resetDocument,
    deleteFee: docOrder.deleteFee,
    checkUploadStatus: docOrder.checkUploadStatus,
    checkingUploads: docOrder.checkingUploads
  };

  // ---- live section gates (server re-validates at each transition) ----
  const manual: ManualClosingFields = {
    powerOfAttorney: order.power_of_attorney,
    signingDate: order.signing_date,
    closingDate: order.closing_date,
    firstPaymentDate: order.first_payment_date,
    docDating: order.doc_dating,
    signType: order.sign_type,
    docEmail: order.doc_email
  };
  const reqFilled = reqFilledCount(manual);
  const vestingConfirmed = !!order.vesting_confirmed;
  const gatesSource = { closingInTrust: !!order.vesting_workflow?.closingInTrust };
  const s4 = section4Gate({ purpose: loanFacts.purpose, propertyType: loanFacts.propertyType }, order.insurance ?? EMPTY_INSURANCE);
  const s5 = section5Gate(fees, invoiceFolderStatus);
  const s6 = section6Gate(derivedDocs, gatesSource);
  const insuranceDeferred = !!order.insurance?.deferred;

  const checks: SignOffCheck[] = [
    { ok: reqFilled === 6 && vestingConfirmed, num: '2', label: 'Closing Details' },
    { ok: true, num: '3', label: 'Settlement & Title' },
    { ok: s4.ok, num: '4', label: 'Insurance', sub: insuranceDeferred ? 'Deferred for submission' : null },
    { ok: s5.ok, num: '5', label: 'Third-Party Fees' },
    { ok: s6.ok, num: '6', label: 'Closing Documents' }
  ];
  const allChecksOk = checks.every((c) => c.ok);
  const signed = !!order.signed_at;
  const canSign = editable && role === 'processor' && allChecksOk;
  const canSubmit = signed && reqFilled === 6 && vestingConfirmed && s5.ok;

  // Insurance defer path: everything except Insurance must be complete. The
  // same §5 gate used by the checklist/sign/submit applies here too.
  const otherReqDocsMissing = derivedDocs.filter(
    (d) => d.required && d.status === 'missing' && d.id !== 'd-eoi' && (!d.gated || (gatesSource as Record<string, unknown>)[d.gated])
  ).length;
  const canDefer = canDeferInsuranceGate({ manual, vestingConfirmed, fees, invoiceFolderStatus, otherReqDocsMissing });
  const eoiDoc = derivedDocs.find((d) => d.id === 'd-eoi');
  const missingInsurance = {
    fields: buildMissingInsuranceFields(order.insurance ?? EMPTY_INSURANCE, loanFacts.purpose, loanFacts.propertyType),
    doc: !(eoiDoc && eoiDoc.status === 'received')
  };

  const onSign = async () => {
    setSignBlockers([]);
    const res = await docOrder.sign();
    if (!res.ok && res.blockers?.length) {
      setSignBlockers(res.blockers);
      addToast(`Finish before signing: ${res.blockers.join(', ')}`, 'alertTriangle');
    } else if (res.ok) {
      addToast('Signed off', 'penTool');
    } else if (res.error) {
      // R4 rev-2 (#3): blocker-less rejections (the POA/Hybrid 422, gate
      // errors) surface verbatim — never a silent dead click on the pad.
      addToast(res.error, 'alertTriangle');
    }
  };

  const goInsurance = () => {
    document.getElementById('sec-insurance')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- closing send gate (Section A1.5) ----
  const sendGate = sendToLoGate({
    fees,
    gatesSource,
    vesting: order.vesting,
    encompassVesting: order.encompass_vesting,
    encompassVestingConfirmed: !!order.encompass_vesting_confirmed
  });
  const sendTip = sendGate.vestingNeedsConfirm
    ? 'Confirm the current vesting per Encompass is accurate in Closing Details before sending.'
    : `Approve all third-party fees first — ${sendGate.unapproved} ${sendGate.unapproved === 1 ? 'fee still needs' : 'fees still need'} approval.`;

  // ---- action bar (role × stage) ----
  const idx = visualStageIndex(order.stage);
  let statusMsg: React.ReactNode;
  let actions: React.ReactNode = null;

  if (role === 'processor') {
    if (order.stage === 'draft') {
      statusMsg = canSubmit ? (
        <>
          <span className="dot ok" />Ready to submit to Closing
        </>
      ) : (
        <>
          <span className="dot warn" />
          {/* Prototype priority chain (Workspace.jsx:367) minus the standalone
              vesting nudge — Jennifer bug 9: the §2 check row already carries it. */}
          {!signed
            ? 'Sign-off required'
            : s5.feesNeedTiming > 0
              ? `${s5.feesNeedTiming} fee${s5.feesNeedTiming === 1 ? '' : 's'} need disposition`
              : s5.creditChoicePending
                ? 'Credit Report — choose Includes Refresh or Refresh not required'
                : 'Complete required fields'}
        </>
      );
      actions = (
        <>
          <Btn
            variant="ghost"
            icon="save"
            disabled={docOrder.saving || docOrder.needsReload}
            onClick={async () => {
              const ok = await docOrder.flush();
              addToast(
                ok ? 'Draft saved' : 'Some changes couldn’t be saved — use Retry save',
                ok ? 'save' : 'alertTriangle'
              );
            }}
          >
            {docOrder.saving ? 'Saving…' : 'Save Draft'}
          </Btn>
          <Btn variant="primary" icon="send" disabled={!canSubmit || docOrder.busy} onClick={() => setModal('submit')}>
            Submit to Closing
          </Btn>
        </>
      );
    } else {
      statusMsg = (
        <>
          <span className="dot info" />
          Submitted — now with Closing. You&rsquo;ll be notified when docs are confirmed.
        </>
      );
    }
  } else if (role === 'closing') {
    if (order.stage === 'submitted') {
      statusMsg = (
        <>
          <span className="dot info" />
          Review the order, then send it to the LO.
        </>
      );
      actions = (
        <>
          {/* R4 #3: the closer edits Closing Details/Vesting at submitted —
              give the queued autosaves an explicit drain, same as the
              processor's Save Draft. */}
          <Btn
            variant="ghost"
            icon="save"
            disabled={docOrder.saving || docOrder.needsReload}
            onClick={async () => {
              const ok = await docOrder.flush();
              addToast(
                ok ? 'Draft saved' : 'Some changes couldn’t be saved — use Retry save',
                ok ? 'save' : 'alertTriangle'
              );
            }}
          >
            {docOrder.saving ? 'Saving…' : 'Save Draft'}
          </Btn>
          <Btn variant="ghost" icon="rotateCcw" disabled={docOrder.busy} onClick={() => void docOrder.returnToProcessor()}>
            Return to Processor
          </Btn>
          <Btn
            variant="ghost"
            icon="eye"
            onClick={() => {
              setPreviewOpen(true);
              void loadLoSummary();
            }}
          >
            Preview Doc Order
          </Btn>
          <span className="send-tip-wrap" data-tip={sendGate.allApproved ? undefined : sendTip}>
            <Btn variant="primary" icon="send" disabled={!sendGate.allApproved || docOrder.busy} onClick={() => setModal('confirm')}>
              Send Doc Order to LO
            </Btn>
          </span>
        </>
      );
    } else if (order.stage === 'draft') {
      statusMsg = (
        <>
          <span className="dot neutral" />
          Waiting on the Processor to submit.
        </>
      );
    } else {
      statusMsg = (
        <>
          <span className="dot ok" />
          Doc Order Completed — {order.stage === 'complete' ? 'acknowledged by the LO.' : 'sent to the LO for review.'}
        </>
      );
    }
  } else {
    // lo — the acknowledgement pad lives in the LoSummary sheet.
    if (order.stage === 'lo_review') {
      statusMsg = (
        <>
          <span className="dot warn" />
          Review the final fees &amp; your compensation, then acknowledge below.
        </>
      );
    } else if (order.stage === 'complete') {
      statusMsg = (
        <>
          <span className="dot ok" />
          Acknowledged. Order complete.
        </>
      );
    } else {
      statusMsg = (
        <>
          <span className="dot neutral" />
          Pending Closing confirmation before your review.
        </>
      );
    }
  }

  const blockerNums = checks.filter((c) => !c.ok).map((c) => c.num);
  const changeRequest = order.lo_change_request;
  const writebackBlocked = !docOrder.writebacks.enabled && (order.stage === 'submitted' || order.stage === 'lo_review');
  const failedEmail = docOrder.emailClaims.find((c) => c.status === 'failed');

  return (
    <DocOrderProvider value={ctxValue}>
      <div className="do-embed">
        <div className="do-embed-bar">
          <div>
            <div className="do-embed-title">
              <Icon name="fileText" size={22} style={{ color: 'var(--accent)' }} />
              Doc Order
            </div>
            <div className="rail-note" style={{ marginTop: 4 }}>
              Finalize third-party fees &amp; closing details so docs are drawn correctly.
            </div>
          </div>
          {/* R5 #1/#5: ALWAYS available while editable — the stale-gated
              button vanished after one click, stranding program/vesting
              corrections (the edge allows draft+submitted stage owners). */}
          {editable && (
            <Btn variant="ghost" icon="refresh" onClick={() => void docOrder.refresh()} disabled={docOrder.refreshing}>
              {docOrder.refreshing ? 'Refreshing…' : 'Refresh from Encompass'}
            </Btn>
          )}
        </div>

        {/* Flow stepper — 5 visual steps over 4 persisted stages (confirmed is projected). */}
        <div className="flow" style={{ marginBottom: 'var(--s-6)' }}>
          {DO_VISUAL_STAGES.map((s, i) => {
            const cls = i < idx ? 'done' : i === idx ? 'current' : 'todo';
            return (
              <div key={s.key} style={{ display: 'contents' }}>
                <div className={`flow-step ${cls}`}>
                  <span className="flow-dot">
                    {i < idx ? <Icon name="check" size={15} strokeWidth={3} /> : <Icon name={STAGE_ICONS[s.key]} size={14} />}
                  </span>
                  <span className="flow-label">
                    <span className="fl-name">{s.label}</span>
                    <span className="fl-owner">{s.owner}</span>
                  </span>
                </div>
                {i < DO_VISUAL_STAGES.length - 1 && <span className={`flow-bar ${i < idx ? 'done' : ''}`} />}
              </div>
            );
          })}
        </div>

        {role === 'closing' && order.stage === 'submitted' && (
          <div className="banner info" style={{ marginBottom: 'var(--s-6)' }}>
            <Icon name="edit" size={16} className="banner-ico" />
            <span>
              Editing as <b>Closing</b> — you can edit Closing Details &amp; Vesting and adjust fees before drawing docs. Return to the Processor or send to the LO when ready.
            </span>
          </div>
        )}
        {!editable && role !== 'lo' && (
          <div className="banner info" style={{ marginBottom: 'var(--s-6)' }}>
            <Icon name="lock" size={16} className="banner-ico" />
            <span style={{ flex: 1 }}>
              This order is <b>{DO_VISUAL_STAGES[idx].label}</b> and read-only at this stage.
            </span>
            {order.stage === 'complete' && (viewer.canReviewSubmitted || viewer.isElevated) && (
              <Btn variant="ghost" icon="rotateCcw" disabled={docOrder.busy} onClick={() => setReopenOpen(true)}>
                Reopen for changes
              </Btn>
            )}
          </div>
        )}
        {role === 'lo' && order.stage === 'complete' && (viewer.canReviewSubmitted || viewer.isElevated) && (
          <div className="banner info" style={{ marginBottom: 'var(--s-6)' }}>
            <Icon name="lock" size={16} className="banner-ico" />
            <span style={{ flex: 1 }}>
              This order is complete — the Loan Officer signed it. Reopening returns it to Closing for changes.
            </span>
            <Btn variant="ghost" icon="rotateCcw" disabled={docOrder.busy} onClick={() => setReopenOpen(true)}>
              Reopen for changes
            </Btn>
          </div>
        )}
        {writebackBlocked && (
          <div className="banner warn" style={{ marginBottom: 'var(--s-6)' }}>
            <Icon name="alertTriangle" size={16} className="banner-ico" />
            <span>
              <b>Encompass write-back is disabled for this loan.</b> {docOrder.writebacks.reason || 'Sending to the LO and acknowledgement are blocked until write-backs are enabled.'}
            </span>
          </div>
        )}
        {failedEmail && (
          <div className="banner warn" style={{ marginBottom: 'var(--s-6)' }}>
            <Icon name="mail" size={16} className="banner-ico" />
            <span style={{ flex: 1 }}>
              A notification failed to send{failedEmail.lastError ? ` (${failedEmail.lastError})` : ''}.
            </span>
            <button type="button" className="recon-btn attn" disabled={docOrder.busy} onClick={() => void docOrder.resendEmail(failedEmail.id)}>
              Resend
            </button>
          </div>
        )}

        {role === 'lo' ? (
          <LoSummary
            order={order}
            fees={fees}
            loanFacts={loanFacts}
            extras={extras}
            capacity={docOrder.capacity}
            loProfile={docOrder.loProfile}
            summaryLoading={docOrder.loSummaryLoading}
            summaryError={docOrder.loSummaryError}
            onRetrySummary={docOrder.loadLoSummary}
            onAcknowledge={() => setModal('ack')}
          />
        ) : (
          <div className="ws-grid no-rail embedded">
            <div className="ws-main">
              {changeRequest && order.stage !== 'complete' && (
                <div className="banner warn" style={{ marginBottom: 14, display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <Icon name="rotateCcw" size={16} className="banner-ico" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <b>Loan Officer requested changes</b> — {changeRequest.by} · {fmtStamp(changeRequest.at)}
                      <div style={{ marginTop: 4, fontWeight: 500 }}>{changeRequest.note}</div>
                      {changeRequest.items.length > 0 && (
                        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                          {changeRequest.items.map((it) => (
                            <li key={it.id}>
                              <b>{it.name}</b>:{' '}
                              {it.amtChanged && (
                                <>
                                  {fmtMoney(it.fromAmount, true)} → <b>{fmtMoney(it.toAmount, true)}</b>
                                </>
                              )}
                              {it.amtChanged && it.reimbChanged ? ' · ' : ''}
                              {it.reimbChanged && (
                                <>
                                  reimburse {it.fromReimburse ? 'on' : 'off'} → <b>{it.toReimburse ? 'on' : 'off'}</b>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {editable && (
                      <button type="button" className="recon-btn neutral" disabled={docOrder.busy} onClick={() => void docOrder.resolveChangeRequest()}>
                        Mark resolved
                      </button>
                    )}
                  </div>
                </div>
              )}

              {renderSections(SECTION_ORDERS[role], { order, fees, derivedDocs, extras, loanFacts })}

              {(order.stage === 'lo_review' || order.stage === 'complete') && <LoCompSection fees={fees} loProfile={docOrder.loProfile} />}

              {role === 'processor' && (
                <SignOffSection
                  checks={checks}
                  signed={signed}
                  signedByName={userName}
                  signedAt={order.signed_at}
                  canSign={canSign}
                  signing={docOrder.signing}
                  blockers={signBlockers.length ? signBlockers : blockerNums}
                  stepNo={SECTION_ORDERS.processor.length + 1}
                  onSign={() => void onSign()}
                  canDeferInsurance={canDefer && !s4.ok}
                  missingInsurance={missingInsurance}
                  onGoInsurance={goInsurance}
                  feeBlockerDetail={fees.flatMap((fee) => [
                    feeDispositionBlocker(fee),
                    feeReconciliationBlocker(fee, invoiceFolderStatus[fee.feeKey] ?? false)
                  ]).filter((reason): reason is string => !!reason)}
                />
              )}
            </div>
          </div>
        )}

        {docOrder.needsReload && (
          <div className="banner warn compact-banner" role="alert" style={{ marginBottom: 'var(--s-3)' }}>
            <Icon name="refresh" size={14} className="banner-ico" />
            <span style={{ flex: 1 }}>
              <b>Saved — reload required.</b> Your last change persisted, but the workspace couldn&rsquo;t refresh and is now stale. Reload to continue; further changes are blocked until then.
            </span>
            <Btn variant="primary" icon="refresh" onClick={() => void docOrder.retryReload()}>
              Reload
            </Btn>
          </div>
        )}
        {docOrder.saveStalled && (
          <div className="banner warn compact-banner" role="alert" style={{ marginBottom: 'var(--s-3)' }}>
            <Icon name="alertTriangle" size={14} className="banner-ico" />
            <span style={{ flex: 1 }}>
              <b>Unsaved changes.</b> The save didn&rsquo;t go through — your edits are safe here. Fix what the message said (or reconnect) and retry.
            </span>
            <Btn variant="ghost" icon="refresh" disabled={docOrder.needsReload} onClick={() => void docOrder.flush()}>
              Retry save
            </Btn>
          </div>
        )}

        <div className="action-bar">
          <div className="ab-status">{statusMsg}</div>
          <div style={{ display: 'flex', gap: 'var(--s-3)' }}>{actions}</div>
        </div>
      </div>

      {reopenOpen && (
        <div className="modal-backdrop" onClick={() => setReopenOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 'var(--r-md)', background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
                  <Icon name="rotateCcw" size={18} />
                </span>
                Reopen for changes
              </div>
            </div>
            <div className="modal-body">
              <p style={{ margin: '4px 0 12px', color: 'var(--fg-muted)', lineHeight: 1.6 }}>
                Returns the order to <b>Closing</b>. The Loan Officer will need to re-review and sign again.
                The previous signed record stays in the history.
              </p>
              <label className="field-label" style={{ display: 'block', marginBottom: 6 }}>
                Why is this order being reopened? <span className="req">*</span>
              </label>
              <textarea
                className="recon-note"
                rows={3}
                maxLength={1000}
                value={reopenReason}
                placeholder="e.g. Title updated the settlement fee after signing — figures need correction."
                onChange={(e) => setReopenReason(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" onClick={() => setReopenOpen(false)}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                icon="rotateCcw"
                disabled={!reopenReason.trim() || docOrder.busy}
                onClick={async () => {
                  const res = await docOrder.reopenOrder(reopenReason.trim());
                  if (res.ok) {
                    setReopenOpen(false);
                    setReopenReason('');
                    addToast('Order reopened — back with Closing', 'rotateCcw');
                  }
                }}
              >
                Reopen order
              </Btn>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="do-preview-overlay" role="dialog" aria-modal="true" aria-label="Preview Doc Order">
          <div className="do-preview-scrim" onClick={() => setPreviewOpen(false)} />
          <div className="do-preview-body">
            <div className="do-preview-hd">
              <span className="do-preview-title">
                <Icon name="eye" size={16} style={{ color: 'var(--accent)' }} />
                Preview Doc Order
              </span>
              <Btn variant="ghost" onClick={() => setPreviewOpen(false)}>
                Close
              </Btn>
            </div>
            <p className="do-preview-note">
              Read-only preview of the summary the Loan Officer receives after you send the order. Nothing here saves or sends.
            </p>
            <div className="do-preview-inert">
              <LoSummary
                order={order}
                fees={fees}
                loanFacts={loanFacts}
                extras={extras}
                capacity={docOrder.capacity}
                loProfile={docOrder.loProfile}
                summaryLoading={docOrder.loSummaryLoading}
                summaryError={docOrder.loSummaryError}
                onAcknowledge={() => {}}
                mode="preview"
              />
            </div>
          </div>
        </div>
      )}

      {modal && (
        <HandoffModal
          type={modal}
          order={order}
          fees={fees}
          derivedDocs={derivedDocs}
          gatesSource={gatesSource}
          loanFacts={loanFacts}
          extras={extras}
          userName={userName}
          busy={docOrder.busy}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            if (modal === 'submit') {
              const res = await docOrder.submit();
              if (res.ok) {
                setModal(null);
                addToast('Submitted to Closing', 'send');
              }
            } else if (modal === 'confirm') {
              const res = await docOrder.sendToLo();
              if (res.ok) {
                setModal(null);
                addToast('Sent to the LO', 'send');
              }
            } else if (modal === 'ack') {
              const res = await docOrder.loAcknowledge();
              if (res.ok) {
                setModal(null);
                addToast(res.pendingWrite ? 'Acknowledged — lender-credit write pending' : 'Final fees acknowledged', 'check');
              }
            }
          }}
        />
      )}
    </DocOrderProvider>
  );
}

// ---------------------------------------------------------------------------
// Section rendering — one node per key, laid out in the role's order.
// ---------------------------------------------------------------------------
interface SectionData {
  order: DocOrderRow;
  fees: DocOrderFee[];
  derivedDocs: ChecklistDoc[];
  extras: ReturnType<typeof buildSnapshotExtras>;
  loanFacts: ReturnType<typeof buildLoanFacts>;
}

function renderSections(orderKeys: string[], data: SectionData): React.ReactNode {
  const stepNoFor: Record<string, number> = {};
  orderKeys.forEach((k, i) => {
    stepNoFor[k] = i + 1;
  });
  const nodes: Record<string, React.ReactNode> = {
    'sec-snapshot': <SnapshotSection key="sec-snapshot" extras={data.extras} closingDate={data.order.closing_date} stepNo={stepNoFor['sec-snapshot']} />,
    'sec-closing': <ClosingVestingSection key="sec-closing" order={data.order} stepNo={stepNoFor['sec-closing']} />,
    'sec-settlement': <SettlementSection key="sec-settlement" settlement={data.order.settlement} docEmail={data.order.doc_email} stepNo={stepNoFor['sec-settlement']} />,
    'sec-insurance': <InsuranceSection key="sec-insurance" insurance={data.order.insurance} stepNo={stepNoFor['sec-insurance']} />,
    'sec-fees': (
      <div key="sec-fees" id="sec-fees" className="ws-section">
        <FeeTable fees={data.fees} stepNo={stepNoFor['sec-fees']} />
      </div>
    ),
    'sec-docs': <DocumentsSection key="sec-docs" docs={data.derivedDocs} fees={data.fees} stepNo={stepNoFor['sec-docs']} />,
    'sec-review': <ClosingReviewSummary key="sec-review" order={data.order} fees={data.fees} docs={data.derivedDocs} loanFacts={data.loanFacts} stepNo={stepNoFor['sec-review']} />
  };
  return orderKeys.map((k) => nodes[k]);
}

// ---------------------------------------------------------------------------
// LO Compensation & Reconciliation — inline for the closing/processor view at
// the LO-review / complete stages (the LO's own sheet carries this itself).
// ---------------------------------------------------------------------------
function LoCompSection({ fees, loProfile }: { fees: DocOrderFee[]; loProfile: { comp: number; bps: number | null; compType: string | null } | null }) {
  const lines = coreReimburseLines(fees);
  const reimburse = coreReimburseTotal(fees);
  if (!loProfile) {
    return (
      <section className="ws-section" id="sec-locomp">
        <div className="card card-pad snapshot-compact lo-comp-card">
          <div className="ws-section-head">
            <h3 className="ws-section-title">
              <span className="ws-step-no gold">$</span>LO Compensation &amp; Reconciliation
            </h3>
          </div>
          <p className="lo-comp-unavailable">Compensation details are unavailable for this order.</p>
        </div>
      </section>
    );
  }
  const baseComp = loProfile.comp;
  const netComp = netLoPayout(baseComp, fees);
  const pac = fees.filter((f) => f.timing === 'PAC').reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const poc = fees.filter((f) => f.timing === 'POC').reduce((s, f) => s + (Number(f.amount) || 0), 0);
  return (
    <section className="ws-section" id="sec-locomp">
      <div className="card card-pad snapshot-compact lo-comp-card">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no gold">$</span>LO Compensation &amp; Reconciliation
          </h3>
          <span className="status-pill gold">
            <Icon name="scale" size={13} />Pushes to Banker Reconciliation
          </span>
        </div>
        <div className="lo-comp-hero">
          <div>
            <div className="lc-label">Net LO payout at closing</div>
            <div className="lc-big">{fmtMoney(netComp)}</div>
            <div className="lc-sub">Base comp + LO-paid reimbursement − E&amp;O</div>
          </div>
          <div className="lc-stack">
            <div className="lc-line">
              <span>
                Base comp{loProfile?.bps !== null && loProfile?.bps !== undefined ? ` (${loProfile.bps} bps · ${loProfile.compType})` : ''}
              </span>
              <b>{fmtMoney(baseComp)}</b>
            </div>
            <div className="lc-line gold-row">
              <span>
                <Icon name="rotateCcw" size={13} /> + LO Reimbursement
              </span>
              <b>{fmtMoney(reimburse, true)}</b>
            </div>
            <div className="lc-line">
              <span>− E&amp;O Insurance</span>
              <b>− {fmtMoney(DO_CONFIG.eoInsurance, true)}</b>
            </div>
            <div className="lc-line total">
              <span>Net payout</span>
              <b>{fmtMoney(netComp)}</b>
            </div>
          </div>
        </div>
        {lines.length > 0 && (
          <div className="lc-reimb-list">
            <div className="field-subhead" style={{ margin: '14px 0 8px', padding: 0, border: 0 }}>
              Reimbursable PAC fees ({lines.length})
            </div>
            {lines.map((r) => (
              <div key={r.key} className="lc-reimb-row">
                <span className="fee-name" style={{ fontSize: 'var(--t-sm)' }}>
                  {r.name}
                  {r.payee && (
                    <span className="fee-payee" style={{ marginLeft: 6 }}>
                      {r.payee}
                    </span>
                  )}
                </span>
                <span className="num" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                  {fmtMoney(r.amount, true)}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="rail-note" style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
          <Icon name="scale" size={13} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
          <span>
            These figures post to the Loan Officer&rsquo;s <b>Banker Reconciliation</b> once the order completes.
          </span>
        </div>
        <div className="rail-row" style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-subtle)' }}>
          <span>
            Fees summary — PAC {fmtMoney(pac, true)} · POC {fmtMoney(poc, true)}
          </span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Handoff modal — submit / confirm carry the loan-facts card + email preview
// rendered from the SAME core builders that send the email; ack is text-only.
// ---------------------------------------------------------------------------
interface HandoffModalProps {
  type: Exclude<ModalType, null>;
  order: DocOrderRow;
  fees: DocOrderFee[];
  derivedDocs: ChecklistDoc[];
  gatesSource: Record<string, unknown>;
  loanFacts: ReturnType<typeof buildLoanFacts>;
  extras: ReturnType<typeof buildSnapshotExtras>;
  userName: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function HandoffModal({ type, order, fees, derivedDocs, gatesSource, loanFacts, extras, userName, busy, onClose, onConfirm }: HandoffModalProps) {
  const reimburse = coreReimburseTotal(fees);
  const cfg = {
    submit: {
      title: 'Submit to Closing',
      icon: 'send',
      cta: 'Submit Order',
      variant: 'primary' as const,
      body: (
        <>
          This locks the order and sends it to <b>Closing</b> to draw documents. Confirmed fees &amp; closing details will be used to generate the loan docs.
        </>
      )
    },
    confirm: {
      title: 'Send Doc Order to LO',
      icon: 'send',
      cta: 'Send to LO',
      variant: 'primary' as const,
      body: (
        <>
          Emails the finalized doc order to the <b>Loan Officer</b> for final review and signature. The order is attached as a PDF, with a portal link that opens their view of the order to sign.
        </>
      )
    },
    ack: {
      title: 'Acknowledge Final Fees',
      icon: 'check',
      cta: 'Acknowledge',
      variant: 'gold' as const,
      body: (
        <>
          You&rsquo;re confirming the final third-party fees and your compensation as shown. This marks the doc order <b>complete</b>.
        </>
      )
    }
  }[type];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal ${type === 'submit' || type === 'confirm' ? 'modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">
            <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 'var(--r-md)', background: 'var(--accent-soft)', color: 'var(--accent)' }}>
              <Icon name={cfg.icon} size={18} />
            </span>
            {cfg.title}
          </div>
        </div>
        <div className="modal-body">
          <p style={{ margin: '4px 0 16px', color: 'var(--fg-muted)', lineHeight: 1.6 }}>{cfg.body}</p>
          <div className="card" style={{ padding: 'var(--s-4)', background: 'var(--bg-subtle)' }}>
            <div className="modal-kv-row">
              <span className="lbl" style={{ color: 'var(--fg-muted)' }}>
                Loan
              </span>
              <span className="val">{loanFacts.loanNumber}</span>
            </div>
            <div className="modal-kv-row" style={{ marginTop: 8 }}>
              <span className="lbl" style={{ color: 'var(--fg-muted)' }}>
                Docs requested by
              </span>
              <span className="val">{fmtDate(order.signing_date)}</span>
            </div>
            <div className="modal-kv-row" style={{ marginTop: 8 }}>
              <span className="lbl" style={{ color: 'var(--fg-muted)' }}>
                Closing date
              </span>
              <span className="val">{fmtDate(order.closing_date)}</span>
            </div>
            {reimburse > 0 && (
              <div className="modal-kv-row" style={{ marginTop: 8 }}>
                <span className="lbl" style={{ color: 'var(--fg-muted)' }}>
                  LO reimbursement
                </span>
                <span className="val" style={{ color: 'var(--gold-fg)' }}>
                  {fmtMoney(reimburse, true)}
                </span>
              </div>
            )}
          </div>
          {type === 'submit' && (
            <EmailPreview label="Notification email preview" content={buildSubmitEmail(order, fees, derivedDocs, gatesSource, loanFacts, extras, userName)} />
          )}
          {type === 'confirm' && (
            <EmailPreview label="Loan Officer email preview" content={buildSendEmail(order.loan_id, loanFacts, extras, userName)} />
          )}
        </div>
        <div className="modal-foot">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant={cfg.variant} icon={cfg.icon} disabled={busy} onClick={onConfirm}>
            {cfg.cta}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// The preview renders the core builder's own HTML — the exact bytes the send
// path uses (esc()-escaped inside the builder), so preview and send can't drift.
function EmailPreview({ label, content }: { label: string; content: { subject: string; html: string } }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div
        className="rail-section-title"
        style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-subtle)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <Icon name="mail" size={12} />
        {label}
      </div>
      <div className="email-preview">
        <div className="email-preview-head">
          <span className="lbl">Subject</span>
          <span className="val subj">{content.subject}</span>
        </div>
        {/* Builder-produced, esc()-escaped email HTML — the same bytes we send. */}
        <div className="email-preview-body" dangerouslySetInnerHTML={{ __html: content.html }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Core-builder inputs — assembled from the order/fees so the modal preview is
// byte-identical to the sent email.
// ---------------------------------------------------------------------------
function buildSubmitEmail(
  order: DocOrderRow,
  fees: DocOrderFee[],
  derivedDocs: ChecklistDoc[],
  gatesSource: Record<string, unknown>,
  loanFacts: ReturnType<typeof buildLoanFacts>,
  extras: ReturnType<typeof buildSnapshotExtras>,
  userName: string
) {
  const ins = order.insurance ?? EMPTY_INSURANCE;
  const needsDic = /fair\s*plan/i.test(ins.company || '') && !ins.dicExtendedCoverages;
  return buildE1Submitted({
    loanNumber: loanFacts.loanNumber,
    borrowerName: loanFacts.borrowers[0] ?? '',
    processorName: extras.processor ?? userName,
    docsRequestedBy: fmtDate(order.signing_date),
    outstanding: buildOutstanding(order, fees, derivedDocs, gatesSource),
    sequence: order.sequence,
    resubmittedAt: order.sequence > 1 ? fmtStamp(order.submitted_at) : null,
    deferred: !!ins.deferred,
    deferReason: ins.deferReason,
    insuranceSnapshot: [
      { label: 'Insurance Company', value: ins.company ?? '' },
      { label: 'Annual Premium', value: ins.premium !== null ? fmtMoney(ins.premium, true) : '' },
      { label: 'Effective Date', value: ins.effective ? fmtDate(ins.effective) : '' },
      { label: 'Expiration Date', value: ins.expiration ? fmtDate(ins.expiration) : '' },
      { label: 'Agent Phone', value: ins.phone ?? '' },
      { label: 'Agent Email', value: ins.email ?? '' }
    ],
    dicSnapshot: needsDic
      ? [
          { label: 'DIC Company', value: ins.dicCompany ?? '' },
          { label: 'Annual Premium', value: ins.dicPremium !== null ? fmtMoney(ins.dicPremium, true) : '' },
          { label: 'Effective Date', value: ins.dicEffective ? fmtDate(ins.dicEffective) : '' },
          { label: 'Expiration Date', value: ins.dicExpiration ? fmtDate(ins.dicExpiration) : '' },
          { label: 'Agent Phone', value: ins.dicPhone ?? '' },
          { label: 'Agent Email', value: ins.dicEmail ?? '' }
        ]
      : undefined,
    hoaCoversWallsIn: ins.hoaMasterCoversWallsIn
  });
}

// loanId is the encompass_loans UUID (order.loan_id) — the deep link MUST match
// the shape the edge send uses (doc-order/index.ts send-to-lo), which keys the
// portal route on loan.id, NOT the human loan number. Preview == send.
function buildSendEmail(
  loanId: string,
  loanFacts: ReturnType<typeof buildLoanFacts>,
  extras: ReturnType<typeof buildSnapshotExtras>,
  userName: string
) {
  return buildE2SentToLo({
    loanNumber: loanFacts.loanNumber,
    borrowerName: loanFacts.borrowers[0] ?? '',
    closerName: userName,
    loFirstName: (extras.loanOfficer ?? 'there').split(' ')[0],
    portalUrl: `portal.lendwisemtg.com/loan-center?loan=${loanId}&tab=docorder`,
    pdfFilename: `${loanFacts.loanNumber}_DocOrder.pdf`
  });
}

// Mirrors the prototype HandoffModal outstanding-items list, keyed off the same
// derived-doc + disposition rules the sign-off gates use.
function buildOutstanding(order: DocOrderRow, fees: DocOrderFee[], derivedDocs: ChecklistDoc[], gatesSource: Record<string, unknown>): OutstandingItem[] {
  const out: OutstandingItem[] = [];
  const closingInTrust = !!order.vesting_workflow?.closingInTrust;
  const trustReviewWaived = !!order.vesting_workflow?.attorneyTrustReviewNotRequired;
  const visibleDocs = derivedDocs.filter((d) => !d.gated || (gatesSource as Record<string, unknown>)[d.gated]);
  if (order.insurance?.deferred) {
    out.push({ tag: 'Deferred', label: 'Insurance section', reason: order.insurance.deferReason || 'No reason provided — please follow up with the processor.' });
  }
  const trustDoc = visibleDocs.find((d) => d.id === 'd-trustrev');
  if (closingInTrust && !(trustDoc && trustDoc.status === 'received') && !trustReviewWaived) {
    out.push({ tag: 'Pending', label: 'Attorney Trust Review — letter not received and no-fee confirmation not entered' });
  }
  visibleDocs
    .filter((d) => d.required && d.status === 'missing' && d.id !== 'd-trustrev')
    .forEach((d) => out.push({ tag: 'Missing', label: `${d.name} not yet received` }));
  const noDisposition = fees.filter((f) => feeNeedsDisposition(f));
  if (noDisposition.length) {
    out.push({ tag: 'Action', label: `${noDisposition.length} fee${noDisposition.length === 1 ? '' : 's'} need disposition (paid by + follow-up)` });
  }
  return out;
}

/** Section-4 fields still blank (drives the sign-off defer prompt). */
function buildMissingInsuranceFields(ins: InsuranceInfo, purpose: string | null, propertyType: string | null): string[] {
  const isPurchase = /purchase/i.test(purpose || '');
  const isCondo = /condo/i.test(propertyType || '');
  if (ins.deferred || (isCondo && ins.hoaMasterCoversWallsIn)) {
    return [];
  }
  const needsDic = /fair\s*plan/i.test(ins.company || '') && !ins.dicExtendedCoverages;
  const miss: string[] = [];
  if (!ins.company) {
    miss.push('Insurance Company');
  }
  if (!ins.premium) {
    miss.push('Annual Premium');
  }
  if (!ins.phone) {
    miss.push('Agent Phone');
  }
  if (isPurchase && !ins.effective) {
    miss.push('Effective Date');
  }
  if (needsDic) {
    if (!ins.dicCompany) {
      miss.push('DIC Company');
    }
    if (!ins.dicPremium) {
      miss.push('DIC Premium');
    }
    if (!ins.dicPhone) {
      miss.push('DIC Agent Phone');
    }
    if (isPurchase && !ins.dicEffective) {
      miss.push('DIC Effective Date');
    }
  }
  return miss;
}
